// EMERGENCY DATA MIGRATION SCRIPT
// Moves data from founder-tenant to the correct user ID tenant

import { db } from '../firebase'
import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  deleteDoc, 
  getDoc,
  writeBatch,
  query,
  where
} from 'firebase/firestore'
import toast from 'react-hot-toast'

export class DataMigrationService {
  
  static async migrateFounderTenantData(userUID) {
    const results = {
      success: false,
      migrated: {},
      errors: [],
      message: ''
    }
    
    try {
      console.log('🔄 Starting data migration from founder-tenant to', userUID)
      toast('🔄 Migrating your data to fix tenant issues...', { duration: 3000 })
      
      // Collections to migrate from founder-tenant subcollections
      const subcollections = [
        'leads',
        'bounce_tracking', 
        'integrations',
        'campaigns',
        'contacts',
        'usage'
      ]
      
      const batch = writeBatch(db)
      let totalMigrated = 0
      
      for (const subcollectionName of subcollections) {
        try {
          console.log(`📁 Migrating ${subcollectionName}...`)
          
          // Get data from founder-tenant subcollection
          const founderCollectionRef = collection(db, 'MarketGenie_tenants', 'founder-tenant', subcollectionName)
          const founderDocs = await getDocs(founderCollectionRef)
          
          if (founderDocs.empty) {
            console.log(`  ✅ ${subcollectionName}: No data to migrate`)
            continue
          }
          
          console.log(`  📍 Found ${founderDocs.size} documents in: MarketGenie_tenants/founder-tenant/${subcollectionName}`)
          let migrated = 0
          
          // Copy each document to the correct user tenant
          founderDocs.forEach((docSnapshot) => {
            const data = docSnapshot.data()
            console.log(`    📄 Migrating document: ${docSnapshot.id}`, Object.keys(data))
            
            // Create document in correct tenant location
            const newDocRef = doc(db, 'MarketGenie_tenants', userUID, subcollectionName, docSnapshot.id)
            batch.set(newDocRef, {
              ...data,
              _migratedFrom: 'founder-tenant',
              _migratedAt: new Date().toISOString(),
              tenantId: userUID // Update tenant ID in the data
            })
            
            // Mark old document for deletion
            batch.delete(docSnapshot.ref)
            
            migrated++
          })
          
          results.migrated[subcollectionName] = migrated
          totalMigrated += migrated
          
          console.log(`  ✅ ${subcollectionName}: ${migrated} documents queued for migration`)
          
        } catch (error) {
          console.error(`❌ Error migrating ${subcollectionName}:`, error)
          results.errors.push(`${subcollectionName}: ${error.message}`)
        }
      }
      
      if (totalMigrated > 0) {
        // Execute the batch migration
        console.log(`🚀 Executing migration of ${totalMigrated} documents...`)
        await batch.commit()
        
        results.success = true
        results.message = `Successfully migrated ${totalMigrated} documents from founder-tenant to ${userUID}`
        
        console.log('✅ Data migration completed successfully!')
        toast.success(`✅ Migrated ${totalMigrated} documents - your data is now unified!`)
        
        // Clear any cached tenant data to force reload
        localStorage.removeItem('marketgenie_current_tenant')
        sessionStorage.removeItem('marketgenie_tenant')
        
      } else {
        results.success = true
        results.message = 'No data found to migrate - all data is already in the correct location'
        console.log('✅ No migration needed - all data already in correct tenant')
        toast('✅ Data already in correct location', { duration: 2000 })
      }
      
    } catch (error) {
      console.error('❌ Migration failed:', error)
      results.success = false
      results.message = `Migration failed: ${error.message}`
      results.errors.push(error.message)
      toast.error(`❌ Migration failed: ${error.message}`)
    }
    
    return results
  }
  
  // Check if founder-tenant has any data that needs migration
  static async checkForMigrationNeeds(userUID) {
    try {
      console.log('🔍 Checking if data migration is needed...')
      
      const subcollections = ['leads', 'bounce_tracking', 'integrations', 'campaigns']
      let foundData = false
      const summary = {}
      
      for (const subcollectionName of subcollections) {
        try {
          const founderCollectionRef = collection(db, 'MarketGenie_tenants', 'founder-tenant', subcollectionName)
          const founderDocs = await getDocs(founderCollectionRef)
          
          if (!founderDocs.empty) {
            foundData = true
            summary[subcollectionName] = founderDocs.size
            console.log(`📊 Found ${founderDocs.size} documents in MarketGenie_tenants/founder-tenant/${subcollectionName}`)
            
            // Log the document names for debugging
            founderDocs.forEach(doc => {
              console.log(`    📄 Document: ${doc.id}`)
            })
          }
        } catch (error) {
          console.log(`  ⚠️ Could not check ${subcollectionName}:`, error.message)
        }
      }
      
      return {
        needsMigration: foundData,
        summary,
        recommendation: foundData 
          ? 'Data migration recommended to consolidate all data under your user ID'
          : 'No migration needed - data is already properly organized'
      }
      
    } catch (error) {
      console.error('Error checking migration needs:', error)
      return {
        needsMigration: false,
        error: error.message
      }
    }
  }
  
  // Remove empty founder-tenant document after successful migration
  static async cleanupFounderTenant() {
    try {
      console.log('🧹 Cleaning up empty founder-tenant document...')
      
      // Check if founder-tenant has any remaining subcollections
      const subcollections = ['leads', 'bounce_tracking', 'integrations', 'campaigns', 'contacts']
      let hasData = false
      
      for (const subcollectionName of subcollections) {
        try {
          const collectionRef = collection(db, 'MarketGenie_tenants', 'founder-tenant', subcollectionName)
          const docs = await getDocs(collectionRef)
          if (!docs.empty) {
            hasData = true
            break
          }
        } catch (error) {
          // Collection doesn't exist or no access - that's fine
        }
      }
      
      if (!hasData) {
        // Safe to remove the founder-tenant document
        const founderTenantRef = doc(db, 'MarketGenie_tenants', 'founder-tenant')
        await deleteDoc(founderTenantRef)
        
        console.log('✅ Cleaned up empty founder-tenant document')
        return { success: true, message: 'Empty founder-tenant document removed' }
      } else {
        console.log('⚠️ founder-tenant still has data, keeping document')
        return { success: false, message: 'founder-tenant still contains data' }
      }
      
    } catch (error) {
      console.error('Error cleaning up founder-tenant:', error)
      return { success: false, error: error.message }
    }
  }
}

export default DataMigrationService